import React, { useState, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Message, Icon, Form } from 'semantic-ui-react'
import { PageHeader } from 'components'
import { AppPrimaryButton } from 'elements'
import styled from 'styled-components'
import { AITemplateService } from 'utility'
import { getRecaptchaToken, ensureRecaptchaLoaded } from 'utility/recaptcha'
import { useTabletAndBelowMediaQuery } from 'layout'

const Container = styled.div`
    max-width: 1000px;
    margin: 0 auto;
    padding: clamp(10px, 2vw, 20px);

    @media (max-width: 930px) {
        padding: 15px;
    }
`

const ReviewSection = styled.div`
    background: #f8f9fa;
    padding: clamp(15px, 2vw, 20px);
    border-radius: 8px;
    margin-bottom: 20px;

    @media (max-width: 930px) {
        padding: 15px;
    }

    h3 {
        font-size: clamp(1.1rem, 2vw, 1.3rem);
        margin-bottom: 15px;
    }

    div {
        font-size: clamp(0.9rem, 1.5vw, 1rem);
        margin-bottom: 8px;
        word-break: break-word;
    }
`

const QuestionCard = styled.div`
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: clamp(15px, 2vw, 20px);
    margin: 15px 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    position: relative;

    @media (max-width: 930px) {
        padding: 15px;
        margin: 10px 0;
    }
`

const QuestionActions = styled.div`
    position: absolute;
    top: 15px;
    right: 15px;
    display: flex;
    gap: 10px;
    z-index: 10;

    @media (max-width: 930px) {
        top: 10px;
        right: 10px;
        gap: 8px;
    }
`

const ActionButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 5px;
    border-radius: 4px;
    transition: background-color 0.2s;

    &:hover {
        background-color: #f0f0f0;
    }

    &.edit {
        color: #007bff;
    }

    &.delete {
        color: #dc3545;
    }

    @media (max-width: 930px) {
        padding: 8px;
    }
`

const ResponseType = styled.span`
    background: #e9ecef;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: clamp(0.75rem, 1.2vw, 0.875rem);
    color: #495057;
`

const BottomActions = styled.div`
    display: flex;
    gap: 15px;
    justify-content: center;
    margin-top: 30px;
    flex-direction: column;
    align-items: center;

    @media (min-width: 931px) {
        flex-direction: row;
    }
`

const YesNoContainer = styled.div`
    display: inline-flex;
    gap: 10px;
    flex-wrap: wrap;

    @media (max-width: 930px) {
        width: 100%;
        justify-content: flex-start;
    }
`

const YesButton = styled.button`
    background: #6fd193;
    color: #ffffff;
    border: none;
    border-radius: 12px;
    padding: clamp(6px, 1.2vw, 8px) clamp(10px, 1.5vw, 14px);
    font-weight: 600;
    font-size: clamp(0.875rem, 1.2vw, 1rem);
    white-space: nowrap;
`

const NoButton = styled.button`
    background: #e05f57;
    color: #ffffff;
    border: none;
    border-radius: 12px;
    padding: clamp(6px, 1.2vw, 8px) clamp(10px, 1.5vw, 14px);
    font-weight: 600;
    font-size: clamp(0.875rem, 1.2vw, 1rem);
    white-space: nowrap;
`

const HeaderActions = styled.div`
    display: flex;
    gap: 15px;
    align-items: center;
    flex-wrap: wrap;

    @media (max-width: 930px) {
        flex-direction: column;
        width: 100%;
        gap: 10px;
    }
`

const ToggleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;

    @media (max-width: 930px) {
        width: 100%;
        justify-content: space-between;
    }
`

const QuestionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 15px;

    @media (max-width: 930px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }

    h3 {
        font-size: clamp(1.1rem, 2vw, 1.3rem);
        margin: 0;
    }
`

const QuestionContent = styled.div`
    margin-bottom: 15px;
    font-size: clamp(0.9rem, 1.5vw, 1rem);
    line-height: 1.5;
    word-break: break-word;
    padding-right: 80px;

    @media (max-width: 930px) {
        padding-right: 70px;
        margin-bottom: 10px;
    }
`

const QuestionFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 15px;

    @media (max-width: 930px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }
`

const RequiredToggle = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;

    @media (max-width: 930px) {
        width: 100%;
        justify-content: space-between;
    }

    strong {
        font-size: clamp(0.875rem, 1.2vw, 1rem);
    }
`

// reCAPTCHA v3 uses invisible token; no widget container needed

export const ReviewAITemplateScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const isMobile = useTabletAndBelowMediaQuery()
    const [questions, setQuestions] = useState([])
    const [editingQuestion, setEditingQuestion] = useState(null)
    const [isAddingQuestion, setIsAddingQuestion] = useState(false)
    const [draftRequired, setDraftRequired] = useState(true)
    const [editingText, setEditingText] = useState('')
    const [editingGuidance, setEditingGuidance] = useState('')
    const [recaptchaToken, setRecaptchaToken] = useState(null)
    const [importing, setImporting] = useState(false)
    const [error, setError] = useState('')
    const [isImported, setIsImported] = useState(false)
    const [success, setSuccess] = useState('')
    const questionRefs = useRef([])
    const draftRef = useRef(null)

    // Get the generated template data from navigation state
    const generatedTemplate = location.state?.generatedTemplate
    const industry = location.state?.industry
    const subcategory = location.state?.subcategory
    const language = location.state?.language || 'en-US'
    const initialQuestionCount = location.state?.questionCount ?? 10
    // We derive count from questions array; not needed separately

    React.useEffect(() => {
        if (generatedTemplate?.Questions) {
            setQuestions(generatedTemplate.Questions)
        } else {
            // If no template data, redirect back to creation
            navigate('/app/templates/create-ai-template')
        }
        // Ensure reCAPTCHA badge is visible
        ensureRecaptchaLoaded()
    }, [generatedTemplate, navigate])

    const handleEditQuestion = index => {
        setIsAddingQuestion(false)
        setDraftRequired(true)
        setEditingQuestion(index)
        setEditingText(questions[index].Text)
        setEditingGuidance(questions[index].Guidance || '')
        // Scroll into view when editing existing question
        setTimeout(() => {
            questionRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
    }

    const clearEditor = () => {
        setEditingQuestion(null)
        setIsAddingQuestion(false)
        setEditingText('')
        setEditingGuidance('')
        setDraftRequired(true)
    }

    const handleSaveEdit = index => {
        const text = editingText.trim()
        if (!text) return
        const updatedQuestions = [...questions]
        updatedQuestions[index] = {
            ...updatedQuestions[index],
            Text: text,
            Guidance: editingGuidance.trim() || null,
        }
        setQuestions(updatedQuestions)
        clearEditor()
    }

    const handleSaveNewQuestion = () => {
        const text = editingText.trim()
        if (!text) return
        const newQuestion = {
            Code: `ABC-${String(questions.length + 1).padStart(3, '0')}`,
            Text: text,
            ResponseType: 'yes_no',
            Required: draftRequired,
            Tags: [industry?.toLowerCase(), subcategory?.toLowerCase()],
            Guidance: editingGuidance.trim() || null,
        }
        setQuestions([...questions, newQuestion])
        clearEditor()
    }

    const handleCancelEdit = () => {
        clearEditor()
    }

    const handleDeleteQuestion = index => {
        const updatedQuestions = questions.filter((_, i) => i !== index)
        setQuestions(updatedQuestions)
        if (editingQuestion === index) {
            clearEditor()
        } else if (editingQuestion !== null && editingQuestion > index) {
            setEditingQuestion(editingQuestion - 1)
        }
    }

    const handleAddQuestion = () => {
        if (isAddingQuestion) {
            draftRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            return
        }
        // Draft stays out of the list until Save. Cancel drops it entirely.
        setEditingQuestion(null)
        setEditingText('')
        setEditingGuidance('')
        setDraftRequired(true)
        setIsAddingQuestion(true)
        setTimeout(() => {
            draftRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 120)
    }

    const handleToggleAllRequired = (checked) => {
        const updatedQuestions = questions.map(q => ({
            ...q,
            Required: checked,
        }))
        setQuestions(updatedQuestions)
    }

    // Check if all questions are currently required
    const allRequired = questions.length > 0 && questions.every(q => q.Required)

    const acquireRecaptchaToken = async () => {
        const token = await getRecaptchaToken('ai_template_import')
        setRecaptchaToken(token)
        return token
    }

    const buildImportPayload = () => ({
        Industry: industry,
        IndustryId: null,
        IndustryCode: null,
        Subcategory: (generatedTemplate?.Subcategory ?? (subcategory ? subcategory.toLowerCase() : '')),
        SubcategoryId: null,
        SubcategoryCode: null,
        TemplateTitle:
            generatedTemplate?.TemplateTitle || `${industry} - ${subcategory} Audit`,
        TemplateDescription: generatedTemplate?.TemplateDescription || '',
        Version: null,
        QuestionCount: questions.length,
        Questions: questions.map(q => ({
            Code: q.Code,
            Text: q.Text,
            ResponseType: q.ResponseType,
            Required: q.Required,
            Tags: q.Tags || [],
            Guidance: q.Guidance || null,
        })),
        Metadata: {
            GeneratedBy: null,
            Language: language,
            Jurisdiction: null,
            Notes: null,
        },
    })

    const hasOpenEditor = editingQuestion !== null || isAddingQuestion

    const handleImportTemplate = async () => {
        if (hasOpenEditor) {
            setError('Save or cancel the open question before importing.')
            return
        }

        const ensuredToken = recaptchaToken || (await acquireRecaptchaToken())
        if (!ensuredToken) {
            setError('reCAPTCHA verification failed. Please try again.')
            return
        }

        try {
            setImporting(true)
            setError('')
            setSuccess('')
            const payload = buildImportPayload()
            const res = await AITemplateService.importTemplate(payload, ensuredToken)
            if (res?.Id || res?.TemplateId) {
                setIsImported(true)
                setSuccess('Template imported successfully!')
                const targetId = res?.TemplateId || String(res?.Id)
                // Wait 2 seconds before navigating to show the disabled state
                setTimeout(() => {
                    navigate(`/app/templates/edit/${targetId}`, { state: { fromAI: true } })
                }, 2000)
            }
        } catch (error) {
            console.error('Import failed:', error)
            setError('Import failed. Please try again.')
        } finally {
            setImporting(false)
        }
    }

    if (!generatedTemplate) {
        return (
            <Container>
                <Message negative>
                    <Message.Header>No Template Data</Message.Header>
                    <p>Please generate a template first.</p>
                </Message>
                <AppPrimaryButton
                    onClick={() =>
                        navigate('/app/templates/create-ai-template', {
                            state: {
                                fromReview: true,
                                industry,
                                subcategory,
                                language,
                                questionCount: initialQuestionCount,
                            },
                        })
                    }
                >
                    Go Back
                </AppPrimaryButton>
            </Container>
        )
    }

    return (
        <Container>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '15px',
                marginBottom: '20px',
            }}>
                <PageHeader header="AI-Based Template Question Creation" />
                <AppPrimaryButton
                    onClick={() =>
                        navigate('/app/templates/create-ai-template', { state: { fromReview: true } })
                    }
                    style={{
                        width: isMobile ? '100%' : 'auto',
                    }}
                >
                    Back
                </AppPrimaryButton>
            </div>

            <ReviewSection>
                <h3>Review Generated Template</h3>
                <div style={{ marginBottom: '10px' }}>
                    <strong>Template Title:</strong>{' '}
                    {generatedTemplate?.TemplateTitle || `${industry} - ${subcategory} Audit`}
                </div>
                <div style={{ marginBottom: '10px' }}>
                    <strong>Industry:</strong> {industry}
                </div>
                <div style={{ marginBottom: '10px' }}>
                    <strong>Sub-Category:</strong> {subcategory}
                </div>
                {generatedTemplate?.TemplateDescription && (
                    <div style={{ marginBottom: '10px' }}>
                        <strong>Template Description:</strong>{' '}
                        {generatedTemplate.TemplateDescription}
                    </div>
                )}
                <div style={{ marginBottom: '10px' }}>
                    <strong>Questions Generated:</strong> {questions.length}
                </div>
            </ReviewSection>

            {error && (
                <Message negative>
                    <Message.Header>Error</Message.Header>
                    <p>{error}</p>
                </Message>
            )}

            {success && (
                <Message success>
                    <Message.Header>Success</Message.Header>
                    <p>{success}</p>
                </Message>
            )}

            <QuestionHeader>
                <h3>Generated Questions</h3>
                <HeaderActions>
                    <ToggleContainer>
                        <Form.Checkbox
                            toggle
                            checked={allRequired}
                            disabled={isImported || importing || questions.length === 0}
                            onChange={(_, data) => handleToggleAllRequired(data.checked)}
                            style={{
                                opacity: (isImported || importing || questions.length === 0) ? 0.5 : 1,
                            }}
                        />
                        <label
                            onClick={() => {
                                if (!isImported && !importing && questions.length > 0) {
                                    handleToggleAllRequired(!allRequired)
                                }
                            }}
                            style={{
                                opacity: (isImported || importing || questions.length === 0) ? 0.5 : 1,
                                cursor: (isImported || importing || questions.length === 0) ? 'not-allowed' : 'pointer',
                                margin: 0,
                                fontWeight: 'bold',
                                userSelect: 'none',
                                fontSize: isMobile ? '0.9rem' : '1rem',
                            }}
                        >
                            {allRequired ? 'Make All Questions Unrequired' : 'Make All Questions Required'}
                        </label>
                    </ToggleContainer>
                    <AppPrimaryButton
                        onClick={handleAddQuestion}
                        disabled={isImported || importing}
                        style={{
                            opacity: (isImported || importing) ? 0.5 : 1,
                            cursor: (isImported || importing) ? 'not-allowed' : 'pointer',
                            width: isMobile ? '100%' : 'auto',
                        }}
                    >
                        + Add Question
                    </AppPrimaryButton>
                </HeaderActions>
            </QuestionHeader>

            {questions.map((question, index) => (
                <QuestionCard key={index} ref={el => { questionRefs.current[index] = el }}>
                    <QuestionActions>
                        <ActionButton
                            className="edit"
                            onClick={() => !isImported && !importing && handleEditQuestion(index)}
                            title="Edit Question"
                            disabled={isImported || importing}
                            style={{
                                opacity: (isImported || importing) ? 0.5 : 1,
                                cursor: (isImported || importing) ? 'not-allowed' : 'pointer',
                            }}
                        >
                            <Icon name="edit" />
                        </ActionButton>
                        <ActionButton
                            className="delete"
                            onClick={() => !isImported && !importing && handleDeleteQuestion(index)}
                            title="Delete Question"
                            disabled={isImported || importing}
                            style={{
                                opacity: (isImported || importing) ? 0.5 : 1,
                                cursor: (isImported || importing) ? 'not-allowed' : 'pointer',
                            }}
                        >
                            <Icon name="trash" />
                        </ActionButton>
                    </QuestionActions>

                    {/* Code hidden per requirement */}

                    <QuestionContent>
                        {editingQuestion === index ? (
                            <div>
                                <textarea
                                    value={editingText}
                                    placeholder="New question text..."
                                    onChange={(e) => setEditingText(e.target.value)}
                                    style={{
                                        width: '100%',
                                        minHeight: '60px',
                                        padding: '8px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: isMobile ? '14px' : '16px',
                                    }}
                                />
                                <div style={{ marginTop: '10px' }}>
                                    <label style={{ fontWeight: 600, display: 'block', marginBottom: 6, fontSize: isMobile ? '0.9rem' : '1rem' }}>Guidance (optional)</label>
                                    <textarea
                                        value={editingGuidance}
                                        onChange={(e) => setEditingGuidance(e.target.value)}
                                        style={{
                                            width: '100%',
                                            minHeight: '50px',
                                            padding: '8px',
                                            border: '1px solid #ddd',
                                            borderRadius: '4px',
                                            fontSize: isMobile ? '13px' : '14px',
                                        }}
                                    />
                                </div>
                                <div style={{ marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                    <AppPrimaryButton
                                        size="small"
                                        onClick={() => handleSaveEdit(index)}
                                        disabled={!editingText.trim()}
                                    >
                                        Save
                                    </AppPrimaryButton>
                                    <AppPrimaryButton
                                        size="small"
                                        onClick={handleCancelEdit}
                                        style={{ background: '#6c757d' }}
                                    >
                                        Cancel
                                    </AppPrimaryButton>
                                </div>
                            </div>
                        ) : (
                            question.Text
                        )}
                    </QuestionContent>

                    <QuestionFooter>
                        {question.ResponseType === 'yes_no' ? (
                            <YesNoContainer>
                                <YesButton>Yes</YesButton>
                                <NoButton>No</NoButton>
                            </YesNoContainer>
                        ) : (
                            <ResponseType>
                                {question.ResponseType}
                            </ResponseType>
                        )}
                        <RequiredToggle>
                            <strong>Required:</strong>
                            <Form.Checkbox
                                toggle
                                checked={!!question.Required}
                                disabled={isImported || importing}
                                onChange={(_, data) => {
                                    const updated = [...questions]
                                    updated[index] = {
                                        ...updated[index],
                                        Required: !!data.checked,
                                    }
                                    setQuestions(updated)
                                }}
                            />
                        </RequiredToggle>
                    </QuestionFooter>

                    {question.Guidance && editingQuestion !== index && (
                        <div
                            style={{
                                marginTop: '10px',
                                padding: '10px',
                                background: '#f8f9fa',
                                borderRadius: '4px',
                                fontSize: isMobile ? '0.9rem' : '1rem',
                                wordBreak: 'break-word',
                            }}
                        >
                            <strong>Guidance:</strong> {question.Guidance}
                        </div>
                    )}
                </QuestionCard>
            ))}

            {isAddingQuestion && (
                <QuestionCard ref={draftRef}>
                    <QuestionActions>
                        <ActionButton
                            className="delete"
                            onClick={handleCancelEdit}
                            title="Discard question"
                        >
                            <Icon name="trash" />
                        </ActionButton>
                    </QuestionActions>

                    <QuestionContent>
                        <div>
                            <textarea
                                value={editingText}
                                placeholder="New question text..."
                                onChange={(e) => setEditingText(e.target.value)}
                                style={{
                                    width: '100%',
                                    minHeight: '60px',
                                    padding: '8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    fontSize: isMobile ? '14px' : '16px',
                                }}
                            />
                            <div style={{ marginTop: '10px' }}>
                                <label style={{ fontWeight: 600, display: 'block', marginBottom: 6, fontSize: isMobile ? '0.9rem' : '1rem' }}>Guidance (optional)</label>
                                <textarea
                                    value={editingGuidance}
                                    onChange={(e) => setEditingGuidance(e.target.value)}
                                    style={{
                                        width: '100%',
                                        minHeight: '50px',
                                        padding: '8px',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        fontSize: isMobile ? '13px' : '14px',
                                    }}
                                />
                            </div>
                            <div style={{ marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                <AppPrimaryButton
                                    size="small"
                                    onClick={handleSaveNewQuestion}
                                    disabled={!editingText.trim()}
                                >
                                    Save
                                </AppPrimaryButton>
                                <AppPrimaryButton
                                    size="small"
                                    onClick={handleCancelEdit}
                                    style={{ background: '#6c757d' }}
                                >
                                    Cancel
                                </AppPrimaryButton>
                            </div>
                        </div>
                    </QuestionContent>

                    <QuestionFooter>
                        <YesNoContainer>
                            <YesButton>Yes</YesButton>
                            <NoButton>No</NoButton>
                        </YesNoContainer>
                        <RequiredToggle>
                            <strong>Required:</strong>
                            <Form.Checkbox
                                toggle
                                checked={draftRequired}
                                onChange={(_, data) => setDraftRequired(!!data.checked)}
                            />
                        </RequiredToggle>
                    </QuestionFooter>
                </QuestionCard>
            )}

            <BottomActions>
                {hasOpenEditor && (
                    <div style={{ color: '#6c757d', textAlign: 'center', fontSize: isMobile ? '0.9rem' : '1rem' }}>
                        Save or cancel the open question before importing.
                    </div>
                )}
                {/* reCAPTCHA v3 token is acquired automatically on import */}
                <AppPrimaryButton
                    color="blue"
                    size="large"
                    onClick={handleImportTemplate}
                    loading={importing}
                    disabled={importing || isImported || hasOpenEditor}
                >
                    {isImported ? 'Imported' : 'Import'}
                </AppPrimaryButton>
            </BottomActions>
        </Container>
    )
}
